import { memo, useCallback } from "react";
import { CellContext } from "@tanstack/react-table";
import {
    Form1099Box,
    isAccountEligibleForForm1099Box,
} from "@common/constants/domains/accounting/form1099Box";
import { WorkspaceAction } from "@common/userWorkspaceRoles";
import { ExtendedAccount } from "@common/types/reports/types";
import { useBulkUpdateAccountsMutation } from "@api/account.api";
import { getApiErrorMessage } from "@api/apiError";
import { showErrorToast } from "@design-system/ToastMessages/useToaster";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { useCanPerformWorkspaceAction } from "@hooks/permissions/useCanPerformWorkspaceAction";
import { useViewOnlyRefusal } from "@hooks/permissions/useViewOnlyRefusal";
import { MEMBER_RESTRICTION_TOOLTIP } from "../constants";
import { ChartOfAccountsRow } from "../utils";
import { getForm1099BoxUpdateTargets } from "./form1099BoxCellHelpers";
import {
    Form1099BoxPicker,
    Form1099BoxReadOnlyCell,
} from "./Form1099BoxPicker";

interface Form1099BoxCellInnerProps {
    account: ExtendedAccount;
    similarAccounts: ExtendedAccount[];
}

const Form1099BoxCellInner = memo(
    ({ account, similarAccounts }: Form1099BoxCellInnerProps) => {
        const canEditCoA = useCanPerformWorkspaceAction(
            WorkspaceAction.COA_EDIT,
        );
        const { isViewOnly, refuseIfViewOnly } = useViewOnlyRefusal();
        const bulkUpdateAccounts = useBulkUpdateAccountsMutation();

        const currentBox = account.form1099Box ?? null;
        const isDisabled =
            (!canEditCoA && !isViewOnly) || bulkUpdateAccounts.isPending;

        const handleValueChange = useCallback(
            (nextBox: Form1099Box | null) => {
                if (refuseIfViewOnly()) {
                    return;
                }
                const targetAccounts = getForm1099BoxUpdateTargets({
                    account,
                    similarAccounts,
                    nextBox,
                });
                if (targetAccounts.length === 0) {
                    return;
                }
                bulkUpdateAccounts.mutate(
                    {
                        accounts: targetAccounts.map((item) => ({
                            accountId: item.id,
                            entityId: item.entityId,
                        })),
                        form1099Box: nextBox,
                    },
                    {
                        onError: (error) => {
                            showErrorToast(
                                getApiErrorMessage(
                                    error,
                                    "Failed to update 1099 box",
                                ),
                            );
                        },
                    },
                );
            },
            [account, bulkUpdateAccounts, refuseIfViewOnly, similarAccounts],
        );

        return (
            <ViewTableCell captureClicks>
                <Form1099BoxPicker
                    value={currentBox}
                    isDisabled={isDisabled}
                    disabledTooltip={
                        !canEditCoA && !isViewOnly
                            ? MEMBER_RESTRICTION_TOOLTIP
                            : undefined
                    }
                    onChange={handleValueChange}
                />
            </ViewTableCell>
        );
    },
);

Form1099BoxCellInner.displayName = "Form1099BoxCellInner";

export const Form1099BoxCell = memo(
    ({
        row,
    }: CellContext<
        ViewTableFeatures,
        ChartOfAccountsRow,
        Form1099Box | null | undefined
    >) => {
        const rowData = row.original;

        if (rowData.classification || !rowData.account) {
            return <ViewTableCell />;
        }

        if (!isAccountEligibleForForm1099Box(rowData.account)) {
            return (
                <Form1099BoxReadOnlyCell value={rowData.account.form1099Box} />
            );
        }

        return (
            <Form1099BoxCellInner
                account={rowData.account}
                similarAccounts={rowData.similarAccounts ?? []}
            />
        );
    },
);

Form1099BoxCell.displayName = "Form1099BoxCell";
